import { differenceInYears } from 'date-fns';
import i18n from '../../../i18n/config';
import { Constants } from '../../constants';
import { HealthGoalType } from '../../model/dto/health-goal-type.enum';
import { StrawStageApiResponse } from '../../model/dto/straw-stage/straw-stage-api-response';
import { UserCycle } from '../../model/dto/user-cycle/user-cycle';
import { UserInfo } from '../../model/dto/user-info';
import {
  SnapshotSource,
  SnapshotTile,
  SnapshotTileKey,
  SnapshotTileState,
  SNAPSHOT_TILE_ORDER,
} from '../../model/type/clinical-snapshot.type';
import { Paginated } from '../../types';
import DateUtils from './date-utils';

const TRACKING_PERIOD_DATE_FORMAT = 'MMM YYYY';

export class ClinicalSnapshotDataUtils {
  static isStagingApplicable(patient?: UserInfo): boolean {
    return patient?.health_goal !== HealthGoalType.TRYING_TO_CONCEIVE;
  }

  static mapToSnapshotTiles(
    patient: UserInfo | undefined,
    reproductiveStage: SnapshotSource<StrawStageApiResponse>,
    cycles: SnapshotSource<Paginated<UserCycle>>
  ): SnapshotTile[] {
    const states: Record<SnapshotTileKey, SnapshotTileState> = {
      age: ClinicalSnapshotDataUtils.getAgeState(patient),
      healthGoal: ClinicalSnapshotDataUtils.getHealthGoalState(patient),
      reproductiveStage: ClinicalSnapshotDataUtils.getReproductiveStageState(
        patient,
        reproductiveStage
      ),
      lastPeriodStart:
        ClinicalSnapshotDataUtils.getLastPeriodStartState(patient),
      knownReproductiveDisorder:
        ClinicalSnapshotDataUtils.getKnownReproductiveDisorderState(patient),
      trackingPeriod: ClinicalSnapshotDataUtils.getTrackingPeriodState(
        patient,
        cycles
      ),
    };

    return SNAPSHOT_TILE_ORDER.map((key) => ({ key, state: states[key] }));
  }

  static getAgeState(patient?: UserInfo): SnapshotTileState {
    if (!patient) {
      return { status: 'loading' };
    }

    if (!patient.day_of_birth) {
      return { status: 'notReported' };
    }

    const dateOfBirth = new Date(patient.day_of_birth);

    if (Number.isNaN(dateOfBirth.getTime())) {
      return { status: 'notReported' };
    }

    return {
      status: 'value',
      value: `${differenceInYears(new Date(), dateOfBirth)}`,
    };
  }

  static getHealthGoalState(patient?: UserInfo): SnapshotTileState {
    if (!patient) {
      return { status: 'loading' };
    }

    return patient.health_goal
      ? { status: 'value', value: patient.health_goal }
      : { status: 'notReported' };
  }

  static getReproductiveStageState(
    patient: UserInfo | undefined,
    reproductiveStage: SnapshotSource<StrawStageApiResponse>
  ): SnapshotTileState {
    if (!ClinicalSnapshotDataUtils.isStagingApplicable(patient)) {
      return { status: 'notApplicable' };
    }

    if (!patient || reproductiveStage.isLoading) {
      return { status: 'loading' };
    }

    if (reproductiveStage.isError || !reproductiveStage.data) {
      return { status: 'error' };
    }

    const { straw_stage, name, total_scan_days } = reproductiveStage.data;

    // A staged patient is the only case with a stage; every content field is null below
    // the threshold, so `name` alone cannot tell staged from unstaged.
    if (straw_stage) {
      return { status: 'value', value: name || straw_stage };
    }

    return total_scan_days > 0
      ? { status: 'notEnoughScans', scanDays: total_scan_days }
      : { status: 'notReported' };
  }

  static getLastPeriodStartState(patient?: UserInfo): SnapshotTileState {
    if (!patient) {
      return { status: 'loading' };
    }

    const formatted = DateUtils.getDateStrFromTimestamp(
      patient.last_first_date_of_period,
      Constants.DATE_DISPLAY_FORMAT
    );

    return formatted
      ? { status: 'value', value: formatted }
      : { status: 'notReported' };
  }

  static getKnownReproductiveDisorderState(
    patient?: UserInfo
  ): SnapshotTileState {
    if (!patient) {
      return { status: 'loading' };
    }

    const disorders = patient.known_reproductive_disorders;

    if (disorders?.length) {
      return {
        status: 'value',
        value: disorders[0],
        secondary:
          disorders.length > 1
            ? i18n.t('patientDetails.snapshot.values.moreDisorders', {
                count: disorders.length - 1,
              })
            : undefined,
      };
    }

    const wasAskedAndAnsweredNo =
      patient.does_have_known_reproductive_disorders === false;

    return wasAskedAndAnsweredNo
      ? { status: 'noneReported' }
      : { status: 'notReported' };
  }

  static getTrackingPeriodState(
    patient: UserInfo | undefined,
    cycles: SnapshotSource<Paginated<UserCycle>>
  ): SnapshotTileState {
    if (!patient || cycles.isLoading) {
      return { status: 'loading' };
    }

    if (cycles.isError) {
      return { status: 'error' };
    }

    const docs = cycles.data?.docs ?? [];

    if (!docs.length) {
      return { status: 'notReported' };
    }

    const cycleCount = cycles.data?.total || docs.length;

    return {
      status: 'value',
      value:
        cycleCount === 1
          ? i18n.t('patientDetails.snapshot.values.cycleCount', {
              count: cycleCount,
            })
          : i18n.t('patientDetails.snapshot.values.cycleCountPlural', {
              count: cycleCount,
            }),
      secondary: ClinicalSnapshotDataUtils.getTrackingPeriodRange(docs),
    };
  }

  static getTrackingPeriodRange(cycles: UserCycle[]): string | undefined {
    const startDays = cycles
      .map((cycle) => cycle.start_day)
      .filter(Boolean)
      .sort();
    const endDays = cycles
      .map((cycle) => cycle.end_day)
      .filter(Boolean)
      .sort();
    const from = DateUtils.getDateStrFromTimestamp(
      startDays[0],
      TRACKING_PERIOD_DATE_FORMAT
    );
    const to = DateUtils.getDateStrFromTimestamp(
      endDays[endDays.length - 1],
      TRACKING_PERIOD_DATE_FORMAT
    );

    // Either bound can be missing or unparseable. Falling through with one of them
    // undefined would interpolate the literal string "undefined" into the range.
    if (!from || !to) {
      return from || to || undefined;
    }

    return from === to ? from : `${from} – ${to}`;
  }
}
